import { buildHeaders } from "src/utils/apiUtils";
import CryptoJS from 'crypto-js';

let BASE_URL = process.env.REACT_APP_API_URL;

export async function loadConfig() {
  try {
    const res = await fetch(`${process.env.PUBLIC_URL}/config.enc.json`, { cache: 'no-store' });
    const { iv, data } = await res.json();

    const key = CryptoJS.SHA256("my-strong-secret");
    const decrypted = CryptoJS.AES.decrypt(data, key, {
      iv: CryptoJS.enc.Base64.parse(iv),
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });

    const configText = decrypted.toString(CryptoJS.enc.Utf8);
    const config = JSON.parse(configText);

    BASE_URL = config.API_URL || BASE_URL;
  } catch (error) {
    // fall back to .env
  }
}

loadConfig().then(() => {});

function handleError(response: Response, data: any, fallback: string): void {
  if (response.status === 401 || response.status === 403 || data?.message === "Forbidden access") {
    throw new Error("Forbidden: You do not have permission to access this resource.");
  }
  throw new Error(data?.message || fallback);
}

async function request(path: string, init: RequestInit, fallback: string, keepRefusal = false) {
  const headers = await buildHeaders(BASE_URL);
  const response = await fetch(`${BASE_URL}${path}`, { headers, credentials: "include", ...init });
  const data = await response.json();
  if (!response.ok) handleError(response, data, fallback);
  // The API answers 200 with { success:false, message } for handled failures, so
  // an ok response is not on its own proof the operation worked. Edit/delete keep
  // that reply (keepRefusal) so the caller can tell refused / held / saved apart.
  if (data && data.success === false && !keepRefusal) throw new Error(data.message || fallback);
  return data;
}

/** GET /remediation — paged list, with optional filters. */
export async function getRemediations(params: {
  query?: string; perPage?: number; pageNo?: number;
  complianceId?: string; referenceId?: string; quarter?: string; year?: string;
  /** 'Not Started' | 'In Progress' | 'Done' */
  status?: string;
  /** 'low' | 'medium' | 'high' | 'critical' — matched case-insensitively. */
  priority?: string;
  /** Approval stage — per-workflow, so pick from getRemediationFilterOptions(). */
  workflowStatus?: string;
  /** Inclusive createdAt range, `YYYY-MM-DD`. */
  dateFrom?: string; dateTo?: string;
  /**
   * Dashboard drill-down bucket — the states the dashboard cards count that a
   * plain `status` cannot express: 'all' | 'not_started' | 'in_progress' |
   * 'done' | 'overdue' | 'urgent' | 'watchlist'.
   */
  bucket?: string;
  /** Inclusive due_date range, `YYYY-MM-DD` — NOT createdAt (dateFrom/dateTo). */
  dueFrom?: string; dueTo?: string;
  /** Column key — 'name' | 'status' | 'priority' | 'due_date' | 'progress'. Ignored when bucket is 'watchlist'. */
  sortBy?: string; sortOrder?: 'asc' | 'desc';
} = {}) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.append(k, String(v));
  });
  return request(`/remediation?${q.toString()}`, { method: 'GET' }, "Failed to fetch remediations");
}

/**
 * GET /remediation/filter-options — the status, priority and approval values the
 * tenant's own rows actually contain. Approval stage names are configured per
 * workflow, so they cannot be a hardcoded list on this side; priorities come back
 * in severity order, not alphabetically.
 */
export async function getRemediationFilterOptions() {
  return request(
    '/remediation/filter-options',
    { method: 'GET' },
    "Failed to fetch remediation filter options",
  );
}

export type ExportScope = 'all' | 'filtered' | 'selected';

/**
 * A column of the findings register. Must match EXPORT_COLUMNS on the server —
 * an unknown key is rejected rather than skipped, so a typo fails loudly rather
 * than quietly returning a sheet with a column missing.
 */
export type ExportColumn =
  | 'number'
  | 'description'
  | 'dueDate'
  | 'status'
  | 'standard'
  | 'function'
  | 'domain'
  | 'control'
  | 'assets'
  | 'observation'
  | 'finding'
  | 'severity'
  | 'comments'
  | 'actionRequired'
  | 'team'
  | 'compensatedControl'
  | 'cbeFeedback';

/**
 * The picker's rows, in the order the sheet writes them.
 *
 * Labels are the header text the column actually produces, so what is ticked
 * here is recognisably what comes out in the file.
 */
export const EXPORT_COLUMN_OPTIONS: { key: ExportColumn; label: string }[] = [
  { key: 'number', label: '#' },
  { key: 'standard', label: 'Standard' },
  { key: 'function', label: 'Function' },
  { key: 'domain', label: 'Domain' },
  { key: 'control', label: 'Control' },
  { key: 'assets', label: 'Assets' },
  { key: 'observation', label: 'Observation' },
  { key: 'finding', label: 'Finding' },
  { key: 'severity', label: 'Severity' },
  { key: 'comments', label: 'Comments' },
  { key: 'actionRequired', label: 'Action Required' },
  // No 'Remediation' row: Action Required writes the plan's name already. What
  // follows is its remaining detail, all of it unticked by default.
  { key: 'description', label: 'Description' },
  { key: 'dueDate', label: 'Due Date' },
  { key: 'status', label: 'Status' },
  { key: 'team', label: 'Team' },
  { key: 'compensatedControl', label: 'compensated control' },
  // Last column of the sheet — the reviewer's note from the remediation's page.
  { key: 'cbeFeedback', label: 'CBE Feedback' },
];

/**
 * Off unless asked for. Unticking Standard does not change the row grouping —
 * rows are always ordered Standard → Function → Domain so the merged boxes stay
 * contiguous, whether or not the standard is written out.
 *
 * Must match OPTIONAL_COLUMNS in the API's remediation-export.dto.ts: Control
 * repeats what Action Required already names, and Description / Due Date /
 * Status are the plan's supporting detail rather than the register's subject.
 */
export const OPTIONAL_EXPORT_COLUMNS: ExportColumn[] = [
  'standard', 'assets', 'control', 'description', 'dueDate', 'status',
];

/** What the picker ticks when it opens: everything not listed above. */
export const DEFAULT_EXPORT_COLUMNS: ExportColumn[] = EXPORT_COLUMN_OPTIONS
  .map((c) => c.key)
  .filter((k) => !OPTIONAL_EXPORT_COLUMNS.includes(k));

export interface ExportRequest {
  columns: ExportColumn[];
  scope: ExportScope;
  format: 'xlsx' | 'csv' | 'json';
  ids?: string[];
  query?: string;
  complianceId?: string;
  referenceId?: string;
  quarter?: string;
  year?: number;
}

/** One assembled worksheet, as returned for the PDF path. */
export interface ExportSheet {
  name: string;
  headers: string[];
  rows: (string | number)[][];
}

export interface ExportMeta {
  totalCount: number;
  totalMatching: number;
  capped: boolean;
}

/**
 * POST /remediation/export for xlsx / csv.
 *
 * Returns the raw blob plus the counts the server reports in headers, so the
 * caller can tell the user how much was written and whether the safety cap cut
 * it short. Errors come back as JSON even though the success path is binary, so
 * a failed response is parsed as text and surfaced.
 */
export async function exportRemediationFile(
  body: ExportRequest,
): Promise<{ blob: Blob; filename: string; meta: ExportMeta }> {
  const headers = await buildHeaders(BASE_URL);
  const response = await fetch(`${BASE_URL}/remediation/export`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const text = await response.text();
    let message = 'Failed to export remediations';
    try {
      message = JSON.parse(text)?.message || message;
    } catch {
      /* not JSON — keep the fallback */
    }
    if (response.status === 401 || response.status === 403) {
      throw new Error('Forbidden: You do not have permission to export this data.');
    }
    throw new Error(message);
  }

  const disposition = response.headers.get('Content-Disposition') || '';
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  return {
    blob: await response.blob(),
    filename: match?.[1] || `remediation-export.${body.format}`,
    meta: {
      totalCount: Number(response.headers.get('X-Total-Count') || 0),
      totalMatching: Number(response.headers.get('X-Total-Matching') || 0),
      capped: response.headers.get('X-Export-Capped') === 'true',
    },
  };
}

/**
 * POST /remediation/export with format 'json'.
 *
 * The backend has no PDF library; this returns the same assembled, filtered,
 * tenant-scoped and permission-checked dataset so the browser can render the PDF
 * with the jsPDF pair already in the bundle. The browser never queries the data
 * itself — only formats what the server sent.
 */
export async function exportRemediationDataset(
  body: Omit<ExportRequest, 'format'>,
): Promise<{ sheets: ExportSheet[]; filename: string; meta: ExportMeta }> {
  const data = await request(
    `/remediation/export`,
    { method: 'POST', body: JSON.stringify({ ...body, format: 'json' }) },
    'Failed to export remediations',
  );
  return {
    sheets: data?.data || [],
    filename: data?.filename || 'remediation-export.pdf',
    meta: {
      totalCount: data?.totalCount ?? 0,
      totalMatching: data?.totalMatching ?? 0,
      capped: Boolean(data?.capped),
    },
  };
}

/** GET /remediation/eligible-compliances — compliances with at least one unfinished task. */
export async function getEligibleCompliances() {
  return request(`/remediation/eligible-compliances`, { method: 'GET' }, "Failed to fetch eligible compliances");
}

/**
 * GET /remediation/eligible-tasks — the outstanding control tasks of the given
 * compliances, each with the supervisor/manager already on it.
 */
export async function getEligibleTasks(complianceIds: string[]) {
  const ids = (complianceIds || []).filter(Boolean);
  return request(
    `/remediation/eligible-tasks?complianceIds=${encodeURIComponent(ids.join(','))}`,
    { method: 'GET' },
    "Failed to fetch control tasks",
  );
}

/** GET /remediation/:id */
export async function getRemediation(id: string) {
  return request(`/remediation/${id}`, { method: 'GET' }, "Failed to fetch remediation");
}

/**
 * POST /remediation — creates the plan, generates its tasks, and starts its
 * approval cycle on `workflowId` (or the tenant's active workflow when omitted).
 */
export async function createRemediation(body: {
  name: string; description?: string; priority?: string; complianceIds: string[];
  /** The controls picked in the form; omitted, every outstanding task is generated. */
  complianceActionIds?: string[];
  /** Supervisor/Manager function for the whole remediation. */
  assign_to_supervisor?: string | null;
  assign_to_manager?: string | null;
  workflowId?: string;
}) {
  return request(`/remediation`, { method: 'POST', body: JSON.stringify(body) }, "Failed to create remediation");
}

/**
 * PATCH /remediation/:id — editing complianceIds re-syncs the task list.
 * Returns the full reply {success, pendingApproval, restarted, message}; see notifyWorkflowResult.
 */
export async function updateRemediation(id: string, body: any) {
  return request(`/remediation/${id}`, { method: 'PATCH', body: JSON.stringify(body) }, "Failed to update remediation", true);
}

/** DELETE /remediation/:id — full reply, like updateRemediation. */
export async function deleteRemediation(id: string) {
  return request(`/remediation/${id}`, { method: 'DELETE' }, "Failed to delete remediation", true);
}

/** GET /remediation/:id/tasks — tasks with their source status and asset sub-tasks. */
/**
 * GET /remediation/:id/tasks — paged and filtered server-side.
 *
 * Every param is optional and omitting them all returns every task, which is what
 * the detail page's derived rows (progress, orphaned, closed-source) need. Pass
 * perPage only where a pager is actually driving the request.
 */
export async function getRemediationTasks(
  id: string,
  params: {
    query?: string; status?: string; priority?: string;
    complianceId?: string; orphaned?: 'yes' | 'no';
    pageNo?: number; perPage?: number;
  } = {},
) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.append(k, String(v));
  });
  const qs = q.toString();
  return request(
    `/remediation/${id}/tasks${qs ? `?${qs}` : ''}`,
    { method: 'GET' },
    "Failed to fetch remediation tasks",
  );
}

/** GET /remediation/:id/progress — derived from remediation task status only. */
export async function getRemediationProgress(id: string) {
  return request(`/remediation/${id}/progress`, { method: 'GET' }, "Failed to fetch remediation progress");
}

/**
 * GET /remediation/:id/activity — audit trail, newest first; paged and filtered
 * server-side. Optional like the task list: no params means every row.
 */
export async function getRemediationActivity(
  id: string,
  params: { action?: string; taskId?: string; pageNo?: number; perPage?: number } = {},
) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.append(k, String(v));
  });
  const qs = q.toString();
  return request(
    `/remediation/${id}/activity${qs ? `?${qs}` : ''}`,
    { method: 'GET' },
    "Failed to fetch remediation activity",
  );
}

/**
 * PATCH /remediation/tasks/:taskId
 * status/priority/notes stay local to the remediation task — the compliance task
 * it came from is never written back to. There is no assignment to send: it is
 * held on the remediation, for the whole plan at once.
 */
export async function updateRemediationTask(taskId: string, body: any) {
  return request(`/remediation/tasks/${taskId}`, { method: 'PATCH', body: JSON.stringify(body) }, "Failed to update task");
}

/**
 * PATCH /remediation/:id/observation
 * Its own route, not part of the remediation PATCH: the observation is written
 * from the view page while the plan is worked, never from the create/edit form.
 * Send '' to clear it.
 */
export async function updateRemediationObservation(id: string, observation: string) {
  return request(
    `/remediation/${id}/observation`,
    { method: 'PATCH', body: JSON.stringify({ observation }) },
    "Failed to save observation",
  );
}

/**
 * PATCH /remediation/:id/cbe-feedback
 * Its own route for the same reason the observation has one: the reviewer writes
 * this WHILE the plan sits on a locked approval stage, and the shared
 * `PATCH /remediation/:id` is gated by that stage — sending it there returned a
 * 403 from a Save button that looked available. Send '' to clear it.
 */
export async function updateRemediationCbeFeedback(id: string, cbe_feedback: string) {
  return request(
    `/remediation/${id}/cbe-feedback`,
    { method: 'PATCH', body: JSON.stringify({ cbe_feedback }) },
    "Failed to save CBE feedback",
  );
}

/**
 * PATCH /remediation/:id/compensated-control
 * Third of the three fields with their own route because they stay writable on a
 * locked approval stage (observation, CBE feedback, compensating control). Send
 * '' to clear it — an unticked checkbox is a real answer.
 */
export async function updateRemediationCompensatedControl(id: string, compensated_control: string) {
  return request(
    `/remediation/${id}/compensated-control`,
    { method: 'PATCH', body: JSON.stringify({ compensated_control }) },
    "Failed to save compensating control",
  );
}

/**
 * GET /remediation/:id/comments
 * One thread for the whole remediation — comments are not per task.
 */
export async function getRemediationComments(id: string) {
  return request(`/remediation/${id}/comments`, { method: 'GET' }, "Failed to fetch comments");
}

/** POST /remediation/:id/comments */
/**
 * POST /remediation/:id/comments
 *
 * `source` says which side is speaking: 'bank' for our own remark (the
 * default) or 'cbe' for the regulator's feedback. The findings register
 * exports one column per side per month, so this is what decides which
 * column the remark lands in.
 */
export async function addRemediationComment(id: string, comment: string, source: 'bank' | 'cbe' = 'bank') {
  return request(
    `/remediation/${id}/comments`,
    { method: 'POST', body: JSON.stringify({ comment, source }) },
    "Failed to add comment",
  );
}

/** DELETE /remediation/tasks/:taskId/assets/:subTaskId — also removes it from the compliance task. */
export async function deleteTaskAsset(taskId: string, subTaskId: string) {
  return request(
    `/remediation/tasks/${taskId}/assets/${subTaskId}`,
    { method: 'DELETE' },
    "Failed to remove asset",
  );
}
